import { Fragment } from "react";
import { Link } from "react-router";
import { cn } from "./cn";

export type Crumb = Readonly<{ label: string; to?: string }>;

/**
 * The trail of a page: "Staff / Customers / Privacy". The last crumb is the current page (`aria-current="page"`, not a link). A crumb has
 * a fixed label, never customer text: the labels end up in the page title and in error reports. The slash between two crumbs is drawn,
 * not read: a screen reader hears the list. On a phone (under 640 px) only the last two crumbs are drawn: four crumbs in 195 px are squeezed to
 * half a word each ("Staf"), so the ones above the parent wait for the wider layouts. They stay in the DOM.
 */
export function Breadcrumbs({
  items,
  className,
}: Readonly<{ items: readonly Crumb[]; className?: string }>) {
  return (
    <nav aria-label="Breadcrumb" className={cn("min-w-0", className)}>
      <ol className="m-0 flex list-none flex-nowrap items-center gap-1.5 p-0 text-sm text-muted-foreground">
        {items.map((item, index) => {
          const last = index === items.length - 1;
          const wide = index < items.length - 2;
          return (
            // biome-ignore lint/suspicious/noArrayIndexKey: the trail is short and never reordered; two crumbs can share a label
            <Fragment key={`${item.label}-${index}`}>
              <li className={cn("min-w-0 items-center", wide ? "hidden sm:flex" : "flex")}>
                {last || item.to === undefined ? (
                  <span
                    aria-current={last ? "page" : undefined}
                    className={cn(
                      "truncate font-medium",
                      last ? "font-semibold text-foreground" : undefined,
                    )}
                  >
                    {item.label}
                  </span>
                ) : (
                  <Link
                    to={item.to}
                    className="inline-flex min-h-11 items-center truncate rounded-xs font-medium no-underline hover:text-foreground hover:underline compact:min-h-8"
                  >
                    {item.label}
                  </Link>
                )}
              </li>
              {last ? null : (
                <li
                  aria-hidden="true"
                  className={cn("shrink-0 text-border", wide ? "hidden sm:block" : undefined)}
                >
                  /
                </li>
              )}
            </Fragment>
          );
        })}
      </ol>
    </nav>
  );
}
